// Phase 90.C.apple — Sign in with Apple (native iOS WebView bridge).
//
// Chỉ render khi:
//   1. Chạy trong native iOS app (window.__pickleagueNativeApp === true &&
//      window.__pickleagueNativePlatform === "ios").
//   2. Apple Guideline 4.8: button style ĐÚNG (black background, Apple
//      logo, text "Đăng nhập với Apple"), NOT "Login with Apple".
//
// Flow: click → postMessage APPLE_SIGN_IN → listen `native-apple-signin`
//   event → POST /api/auth/apple/verify-id-token → refreshMe + navigate.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppleLogo } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

export default function AppleSignInButton({ label = "Đăng nhập với Apple" }) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isNative = window.__pickleagueNativeApp === true;
    const isIos = window.__pickleagueNativePlatform === "ios";
    setVisible(isNative && isIos);
  }, []);

  const handleClick = useCallback(() => {
    if (!window.ReactNativeWebView) {
      toast.error("Native bridge không khả dụng.");
      return;
    }
    setLoading(true);
    // Diagnostic toast — user luôn thấy phản hồi ngay khi tap, kể cả khi
    // native bridge bị delay/error.
    toast.info("Đang mở đăng nhập Apple…", { duration: 2500 });
    const onMsg = async (e) => {
      const detail = e?.detail || {};
      window.removeEventListener("native-apple-signin", onMsg);
      clearTimeout(timeoutHandle);
      if (detail.unsupported) {
        if (mountedRef.current) setLoading(false);
        toast.error("Thiết bị không hỗ trợ Sign in with Apple. Cần iOS 13+ và build có `expo-apple-authentication`.");
        return;
      }
      if (detail.cancelled) {
        if (mountedRef.current) setLoading(false);
        return;
      }
      if (detail.error) {
        if (mountedRef.current) setLoading(false);
        toast.error("Đăng nhập Apple thất bại: " + detail.error);
        return;
      }
      if (!detail.id_token) {
        if (mountedRef.current) setLoading(false);
        toast.error("Không nhận được token từ Apple.");
        return;
      }
      try {
        await api.post("/auth/apple/verify-id-token", {
          id_token: detail.id_token,
          first_name: detail.first_name || null,
          last_name: detail.last_name || null,
        });
        await refreshMe();
        toast.success("Đăng nhập Apple thành công!");
        navigate("/", { replace: true });
      } catch (err) {
        const dt = err?.response?.data?.detail;
        toast.error(typeof dt === "string" ? dt : dt?.message || "Đăng nhập Apple thất bại.");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };
    window.addEventListener("native-apple-signin", onMsg);
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "APPLE_SIGN_IN" }));
    // Safety timeout: 30s — nếu native không phản hồi, báo user thay vì
    // để loading vô tận. Trước đây 60s → user hiểu là "không phản ứng gì".
    const timeoutHandle = setTimeout(() => {
      window.removeEventListener("native-apple-signin", onMsg);
      if (mountedRef.current) {
        setLoading(false);
        toast.error("Apple Sign-In không phản hồi. Vui lòng thử lại hoặc dùng đăng nhập bằng SĐT/email.");
      }
    }, 30000);
  }, [refreshMe, navigate]);

  if (!visible) return null;

  // Apple Guideline 4.8: solid black background, white text, Apple logo.
  // KHÔNG dùng gradient, KHÔNG đổi text. Corner radius match rounded-[18px]
  // để consistent với Google button.
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className="w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-[18px] bg-black text-white font-black text-[14px] active:scale-[0.98] transition hover:bg-black/90 shadow-[0_4px_18px_rgba(0,0,0,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid="apple-signin-btn"
      title="Đăng nhập bằng Apple ID. Bảo vệ quyền riêng tư — bạn có thể ẩn email."
    >
      <AppleLogo size={18} weight="fill" className="text-white" />
      {loading ? "Đang đăng nhập…" : label}
    </button>
  );
}
